import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import SmsSegmentCounter from './SmsSegmentCounter';
import SmsRegistrationForm from './SmsRegistrationForm';
import SmsPolicyUrls from './SmsPolicyUrls';

// Settings > Texting and Email (SMS Phase 2). Owner and admin, Pro and up; the
// parent decides whether to render it at all (sms_enabled, isAdmin, plan).
//
// It renders its two cards only. The parent supplies the collapsible group
// around them, which also holds the Season Check-in card, and passes
// `expanded` so the settings load only once the group is opened.
//
// It chooses how texts go out (Off, a SnowTrack number, or the company's own
// Twilio account), stores BYO credentials, and edits the Storm Mode arrival
// alert. The one thing that sends is the owner's "Send a test text to my
// phone" (POST /api/sms/test), to the number on the owner's own profile.
//
// SMS Phase 6a: in "SnowTrack number" mode the owner presses "Set up my
// texting number", reads what it costs (the one-time setup fee charged now,
// the monthly texting-number line once carriers approve the number), and
// confirms. POST /api/sms/provision charges the fee; the card then shows the
// server's provisioning line. With SMS_PROVISIONING off the server reports
// provisioning unavailable and nothing here shows.
//
// SMS Phase 6b: once the number is attached, the owner fills in the business
// details for carrier registration (SmsRegistrationForm) right here.
//
// THE AUTH TOKEN IS WRITE-ONLY. It goes up once in PUT /api/sms/byo and the
// field is cleared whatever the answer. Every view afterwards is built from
// last-four values the server returns; the page never holds the token again.
const API = `${process.env.REACT_APP_API_URL}/api/sms`;

// How far ahead the arrival alert goes (migration 108), the values the server
// accepts in PUT /api/sms/arrival as leadStops.
const ARRIVAL_LEAD_OPTIONS = [
  { value: 1, label: 'Next stop only' },
  { value: 2, label: 'Next 2 stops' },
  { value: 3, label: 'Next 3 stops' },
];

// The counter estimates {{stops_away}} at its longest value, as the server
// does in PUT /api/sms/arrival.
const STOPS_AWAY_LONGEST = '3 stops';
const withLongestStopsAway = (text) => String(text || '').replace(/\{\{\s*stops_away\s*\}\}/gi, STOPS_AWAY_LONGEST);

const MODE_OPTIONS = [
  { id: 'off', label: 'Off', hint: 'No texts are sent.' },
  { id: 'provisioned', label: 'SnowTrack number', hint: 'SnowTrack provides a local number and handles carrier registration.' },
  { id: 'byo', label: 'My own Twilio', hint: 'Texts go out through your own Twilio account and Messaging Service.' },
];

const A2P_LABELS = {
  VERIFIED: 'Approved',
  IN_PROGRESS: 'In review',
  PENDING: 'In review',
  FAILED: 'Rejected',
};

const EMPTY_FORM = { accountSid: '', authToken: '', messagingServiceSid: '', configureWebhooks: true };

const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

// Why the test button is disabled, or null when it can be used.
const testTextBlocker = (settings) => {
  if (settings.mode === 'off') return 'Choose how texts are sent first.';
  if (settings.status === 'pending_registration') {
    return 'Available once carrier registration is approved. Until then no text can be delivered.';
  }
  if (settings.status !== 'active') return 'Available once texting is active.';
  return null;
};

function SmsSettingsSection({ expanded, isOwner = false }) {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(null);
  // The radio can show "My own Twilio" before anything is saved, so the form
  // appears; the server's mode only becomes byo once credentials are stored.
  const [selectedMode, setSelectedMode] = useState('off');
  const [replacing, setReplacing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [arrivalEnabled, setArrivalEnabled] = useState(false);
  const [arrivalTemplate, setArrivalTemplate] = useState('');
  const [arrivalLead, setArrivalLead] = useState(1);
  // The skip notice (migration 114): saved with the arrival alert.
  const [skipEnabled, setSkipEnabled] = useState(true);
  const [skipTemplate, setSkipTemplate] = useState('');
  const [confirmingSetup, setConfirmingSetup] = useState(false);
  const templateRef = useRef(null);
  const skipTemplateRef = useRef(null);

  const apply = useCallback((data) => {
    setSettings(data);
    setSelectedMode(data.mode);
    setArrivalEnabled(data.arrival.enabled);
    setArrivalTemplate(data.arrival.template || data.arrival.defaultTemplate);
    setArrivalLead(data.arrival.leadStops || 1);
    if (data.skipNotice) {
      setSkipEnabled(data.skipNotice.enabled);
      setSkipTemplate(data.skipNotice.template || data.skipNotice.defaultTemplate);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      apply(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load text messaging settings');
    }
  }, [apply]);

  useEffect(() => {
    if (expanded && !settings) load();
  }, [expanded, settings, load]);

  const run = async (label, fn, okMessage) => {
    setBusy(label);
    setError('');
    setSuccess('');
    try {
      const { data } = await fn();
      apply(data);
      if (okMessage) setSuccess(okMessage);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Try again.');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const chooseMode = async (mode) => {
    if (mode === 'byo' && !settings.byo.credentialsStored) {
      setSelectedMode('byo');
      setReplacing(true);
      return;
    }
    setReplacing(false);
    setSelectedMode(mode);
    const ok = await run('mode', () => axios.put(`${API}/settings/mode`, { mode }), 'Text messaging mode saved.');
    if (!ok) setSelectedMode(settings.mode);
  };

  const saveByo = async (e) => {
    e.preventDefault();
    const body = { ...form };
    // Cleared before the request resolves, success or not.
    setForm((f) => ({ ...f, authToken: '' }));
    const ok = await run('byo', () => axios.put(`${API}/byo`, body), 'Twilio account connected.');
    if (ok) {
      setForm(EMPTY_FORM);
      setReplacing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your Twilio account? Your stored credentials are deleted and texting turns off.')) return;
    const ok = await run('disconnect', () => axios.delete(`${API}/byo`), 'Twilio account disconnected.');
    if (ok) setReplacing(false);
  };

  const saveArrival = async () => {
    const template = arrivalTemplate.trim() === settings.arrival.defaultTemplate ? '' : arrivalTemplate;
    const body = { enabled: arrivalEnabled, template, leadStops: arrivalLead };
    if (settings.skipNotice) {
      body.skipNoticeEnabled = skipEnabled;
      body.skipNoticeTemplate = skipTemplate.trim() === settings.skipNotice.defaultTemplate ? null : skipTemplate;
    }
    await run('arrival', () => axios.put(`${API}/arrival`, body), 'Arrival alert saved.');
  };

  // Puts {{field}} at the cursor of one of the two templates.
  const insertInto = (ref, text, setText, field) => {
    const token = `{{${field}}}`;
    const el = ref.current;
    if (!el) {
      setText((t) => t + token);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + token + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const insertField = (field) => insertInto(templateRef, arrivalTemplate, setArrivalTemplate, field);
  const insertSkipField = (field) => insertInto(skipTemplateRef, skipTemplate, setSkipTemplate, field);

  const requestNumber = async () => {
    setBusy('provision');
    setError('');
    setSuccess('');
    const reinstating = Boolean(settings?.provisioning?.reinstatement);
    const free = reinstating && settings?.provisioning?.reinstatementFeeCents === 0;
    try {
      await axios.post(`${API}/provision`);
      setConfirmingSetup(false);
      await load();
      setSuccess(free
        ? 'Requested. Your new texting number is on its way.'
        : `${reinstating ? 'Reinstatement' : 'Setup'} fee paid. Your texting number is on its way.`);
    } catch (err) {
      // Reloaded first: load() clears the error line on success.
      await load();
      setError(err.response?.data?.error || 'Your texting number could not be requested. Try again.');
    } finally {
      setBusy(null);
    }
  };

  // SMS Phase 6b: after the business details are saved, the provisioning line
  // and the form's visibility come from the server again.
  const registrationSaved = async (data) => {
    await load();
    if (data.submitError) setError(data.submitError);
    else setSuccess(data.submitted
      ? 'Business details sent to the carriers.'
      : 'Business details saved. SnowTrack will send them to the carriers.');
  };

  // SMS Phase 6c: the policy pages alone. Nothing goes to the carriers from
  // here; the campaign step (or SnowTrack's Retry) sends them.
  const policyUrlsSaved = (r) => {
    setError('');
    setSuccess(r.privacyPolicyUrl
      ? 'Policy pages saved. Your campaign will use your own pages.'
      : 'Policy pages saved. Your campaign will use the pages SnowTrack hosts for you.');
  };

  // SMS Phase 6b: a Sole Proprietor's registry code, sent again to the
  // owner's own mobile (three an hour, server side).
  const resendOtp = async () => {
    setBusy('otp');
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API}/registration/otp-resend`);
      setSuccess('A new code is on its way to your mobile. Reply YES to it within 24 hours.');
    } catch (err) {
      setError(err.response?.data?.error || 'The code could not be sent again. Try again later.');
    } finally {
      setBusy(null);
    }
  };

  const sendTestText = async () => {
    setBusy('test');
    setError('');
    setSuccess('');
    try {
      const { data } = await axios.post(`${API}/test`);
      setSuccess(`Test text sent to your number ending ${data.toLast4}.`);
    } catch (err) {
      setError(err.response?.data?.error || 'The test text could not be sent.');
    } finally {
      setBusy(null);
    }
  };

  const showByoForm = selectedMode === 'byo' && (replacing || !settings?.byo.credentialsStored);
  const testBlocker = settings ? testTextBlocker(settings) : null;
  const provisioning = settings?.provisioning;
  // SMS Phase 6c: after a release the same button charges the reinstatement fee.
  const feeCents = provisioning?.reinstatement ? provisioning.reinstatementFeeCents : provisioning?.setupFeeCents;
  // The super admin can set the reinstatement fee to 0, which charges nothing.
  const freeReinstatement = Boolean(provisioning?.reinstatement) && feeCents === 0;

  return (
    <>
      <div className="card mb-lg">
        <div className="card-header">
          <h2 className="card-title">How texts are sent</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
            Texts only go to customers who agreed to receive them.
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        {!settings && !error && <p className="text-muted">Loading...</p>}

        {settings && (
          <>
            <p style={{ fontWeight: 500 }} data-testid="sms-status-text">{settings.statusText}</p>

            <fieldset style={{ border: 'none', padding: 0, margin: '12px 0' }} disabled={busy !== null}>
              <legend className="form-label">Mode</legend>
              {MODE_OPTIONS.map((opt) => (
                <label key={opt.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="sms-mode"
                    value={opt.id}
                    checked={selectedMode === opt.id}
                    onChange={() => chooseMode(opt.id)}
                  />
                  <span>
                    <span style={{ fontWeight: 500 }}>{opt.label}</span>
                    <span className="text-muted" style={{ display: 'block', fontSize: '0.8125rem' }}>{opt.hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            {selectedMode === 'provisioned' && (
              <dl className="sms-detail-list" style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: '0.875rem' }}>
                <dt className="text-muted">Status</dt>
                <dd style={{ margin: 0 }}>{settings.statusText}</dd>
                <dt className="text-muted">Number</dt>
                <dd style={{ margin: 0 }}>{settings.phoneNumber ? formatPhoneDisplay(settings.phoneNumber) : 'Not assigned yet'}</dd>
                <dt className="text-muted">Carrier registration</dt>
                <dd style={{ margin: 0 }}>
                  {settings.a2pStatus ? (A2P_LABELS[settings.a2pStatus] || settings.a2pStatus) : (settings.status === 'active' ? 'Approved' : 'Pending')}
                </dd>
              </dl>
            )}

            {selectedMode === 'provisioned' && settings.mode === 'provisioned' && provisioning?.available && (
              <div style={{ marginTop: '12px' }} data-testid="sms-provision-block">
                {provisioning.line && (
                  <p style={{ fontSize: '0.875rem', margin: '0 0 8px' }} data-testid="sms-provision-line">{provisioning.line}</p>
                )}
                {provisioning.canResendOtp && isOwner && (
                  <button type="button" className="btn btn-secondary" onClick={resendOtp} disabled={busy !== null}>
                    {busy === 'otp' ? 'Sending...' : 'Resend the code'}
                  </button>
                )}
                {provisioning.registrationEditable && isOwner && (
                  <SmsRegistrationForm onSaved={registrationSaved} />
                )}
                {provisioning.policyUrlsEditable && isOwner && (
            <SmsPolicyUrls onSaved={policyUrlsSaved} />
          )}
          {provisioning.registrationEditable && !isOwner && (
                  <p className="text-muted" style={{ fontSize: '0.8125rem' }}>The account owner enters the business details for carrier registration.</p>
                )}
                {provisioning.canRequest && !isOwner && (
                  <p className="text-muted" style={{ fontSize: '0.8125rem' }}>The account owner can set up a texting number.</p>
                )}
                {provisioning.canRequest && isOwner && !confirmingSetup && (
                  <button type="button" className="btn btn-primary" onClick={() => setConfirmingSetup(true)}
                    disabled={busy !== null} data-testid="sms-provision-button">
                    {provisioning.reinstatement ? 'Set up a new texting number' : 'Set up my texting number'}
                  </button>
                )}
                {provisioning.canRequest && isOwner && confirmingSetup && (
                  <div className="alert" style={{ border: '1px solid var(--border-color, #e5e7eb)' }} data-testid="sms-provision-confirm">
                    <p style={{ margin: '0 0 8px' }}>
                      {provisioning.reinstatement
                        ? 'Your earlier texting number was released. SnowTrack gets a new local number for your business and registers it with the phone carriers again.'
                        : 'SnowTrack gets a local texting number for your business and registers it with the phone carriers.'}
                    </p>
                    <ul style={{ margin: '0 0 8px', paddingLeft: '20px', fontSize: '0.875rem' }}>
                      {provisioning.reinstatement && freeReinstatement && <li>There is no reinstatement fee: nothing is charged now.</li>}
                      {provisioning.reinstatement && !freeReinstatement && <li>A one-time reinstatement fee of <strong>{dollars(feeCents)}</strong> is charged now to the card on your subscription, instead of the setup fee.</li>}
                      {!provisioning.reinstatement && <li>A one-time setup fee of <strong>{dollars(feeCents)}</strong> is charged now to the card on your subscription.</li>}
                      <li>Once carriers approve your number, a <strong>{dollars(provisioning.monthlyLineCents)} a month</strong> texting-number line is added to your subscription.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-primary" onClick={requestNumber} disabled={busy !== null}
                        data-testid="sms-provision-confirm-button">
                        {busy === 'provision'
                          ? (freeReinstatement ? 'Requesting...' : 'Charging...')
                          : (freeReinstatement ? 'Set up a new number' : `Pay ${dollars(feeCents)} and set up`)}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setConfirmingSetup(false)} disabled={busy !== null}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedMode === 'byo' && settings.byo.credentialsStored && !replacing && (
              <div>
                <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: '0.875rem' }}>
                  <dt className="text-muted">Account</dt>
                  <dd style={{ margin: 0 }}>AC...{settings.byo.accountSidLast4}</dd>
                  <dt className="text-muted">Auth Token</dt>
                  <dd style={{ margin: 0 }}>token ending {settings.byo.authTokenLast4}</dd>
                  <dt className="text-muted">Messaging Service</dt>
                  <dd style={{ margin: 0 }}>MG...{settings.byo.messagingServiceSidLast4}</dd>
                  <dt className="text-muted">Number</dt>
                  <dd style={{ margin: 0 }}>{settings.phoneNumber ? formatPhoneDisplay(settings.phoneNumber) : 'None on this Messaging Service'}</dd>
                  <dt className="text-muted">10DLC campaign</dt>
                  <dd style={{ margin: 0 }}>{settings.a2pStatus ? (A2P_LABELS[settings.a2pStatus] || settings.a2pStatus) : 'None found'}</dd>
                </dl>
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setReplacing(true)} disabled={busy !== null}>Replace</button>
                  <button type="button" className="btn btn-danger" onClick={disconnect} disabled={busy !== null}>
                    {busy === 'disconnect' ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
              </div>
            )}

            {showByoForm && (
              <form onSubmit={saveByo} autoComplete="off" style={{ marginTop: '8px' }}>
                {!settings.encryptionConfigured && (
                  <div className="alert alert-error">Twilio credentials cannot be stored yet. Contact SnowTrack support.</div>
                )}
                <div className="form-group">
                  <label className="form-label" htmlFor="sms-account-sid">Account SID</label>
                  <input id="sms-account-sid" className="form-input" value={form.accountSid} placeholder="AC..."
                    onChange={(e) => setForm({ ...form, accountSid: e.target.value })} spellCheck={false} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="sms-auth-token">Auth Token</label>
                  <input id="sms-auth-token" className="form-input" type="password" value={form.authToken}
                    autoComplete="new-password" onChange={(e) => setForm({ ...form, authToken: e.target.value })} spellCheck={false} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="sms-ms-sid">Messaging Service SID</label>
                  <input id="sms-ms-sid" className="form-input" value={form.messagingServiceSid} placeholder="MG..."
                    onChange={(e) => setForm({ ...form, messagingServiceSid: e.target.value })} spellCheck={false} />
                </div>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '0.875rem', marginBottom: '12px' }}>
                  <input type="checkbox" checked={form.configureWebhooks && settings.webhookConfigurable}
                    disabled={!settings.webhookConfigurable}
                    onChange={(e) => setForm({ ...form, configureWebhooks: e.target.checked })} />
                  <span>
                    Let SnowTrack configure my Messaging Service
                    <span className="text-muted" style={{ display: 'block', fontSize: '0.8125rem' }}>
                      Points its incoming-message and delivery-status addresses at SnowTrack, so replies and STOP requests reach us.
                      {!settings.webhookConfigurable && ' Not available yet.'}
                    </span>
                  </span>
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button type="submit" className="btn btn-primary" disabled={busy !== null || !settings.encryptionConfigured}>
                    {busy === 'byo' ? 'Checking with Twilio...' : 'Save'}
                  </button>
                  {settings.byo.credentialsStored && (
                    <button type="button" className="btn btn-secondary" onClick={() => { setReplacing(false); setForm(EMPTY_FORM); }}>Cancel</button>
                  )}
                </div>
              </form>
            )}

            {isOwner && (
              <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
                <button type="button" className="btn btn-secondary" onClick={sendTestText}
                  disabled={busy !== null || testBlocker !== null} data-testid="sms-test-button">
                  {busy === 'test' ? 'Sending...' : 'Send a test text to my phone'}
                </button>
                <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '6px' }} data-testid="sms-test-hint">
                  {testBlocker || 'Sends one text to the mobile number on your profile.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {settings && (
        <div className="card mb-lg">
          <div className="card-header">
            <h2 className="card-title">Storm Mode arrival alert</h2>
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
              A text to the customer when a driver heads to their property. Customers without text consent get an email instead, when they have one.
            </p>
          </div>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
            <input type="checkbox" checked={arrivalEnabled} onChange={(e) => setArrivalEnabled(e.target.checked)} />
            <span>Send an arrival alert</span>
          </label>
          <div className="form-group">
            <label className="form-label" htmlFor="sms-arrival-lead">Text customers when the driver is</label>
            <select id="sms-arrival-lead" className="form-input" value={arrivalLead}
              onChange={(e) => setArrivalLead(Number(e.target.value))}>
              {ARRIVAL_LEAD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
              The text goes out when the driver completes or skips a stop, or taps Navigate or On my way in Storm Mode. Each stop gets it once.
            </p>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="sms-arrival-template">Message</label>
            <textarea id="sms-arrival-template" ref={templateRef} className="form-input" rows={3}
              value={arrivalTemplate} onChange={(e) => setArrivalTemplate(e.target.value)} />
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
              {settings.arrival.mergeFields.map((field) => (
                <button key={field} type="button" className="btn btn-secondary btn-sm" onClick={() => insertField(field)}>
                  {`{{${field}}}`}
                </button>
              ))}
            </div>
            <SmsSegmentCounter text={withLongestStopsAway(arrivalTemplate)} maxSegments={settings.arrival.maxSegments} />
          </div>
          {settings.skipNotice && (
            <div className="form-group" data-testid="sms-skip-notice">
              <h3 className="form-label" style={{ marginBottom: '4px' }}>When an alerted stop is skipped</h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
                If the driver skips a stop after its customer got the arrival alert, they get one follow-up right away, the same way the alert went (text, or email). Each stop gets it once.
              </p>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                <input type="checkbox" checked={skipEnabled} onChange={(e) => setSkipEnabled(e.target.checked)} />
                <span>Tell the customer when their stop is skipped</span>
              </label>
              <label className="form-label" htmlFor="sms-skip-template">Skip message</label>
              <textarea id="sms-skip-template" ref={skipTemplateRef} className="form-input" rows={3}
                value={skipTemplate} onChange={(e) => setSkipTemplate(e.target.value)} />
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                {settings.skipNotice.mergeFields.map((field) => (
                  <button key={field} type="button" className="btn btn-secondary btn-sm" onClick={() => insertSkipField(field)}>
                    {`{{${field}}}`}
                  </button>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSkipTemplate(settings.skipNotice.defaultTemplate)}>
                  Use default skip text
                </button>
              </div>
              <SmsSegmentCounter text={skipTemplate} maxSegments={settings.skipNotice.maxSegments} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={saveArrival} disabled={busy !== null}>
              {busy === 'arrival' ? 'Saving...' : 'Save arrival alert'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setArrivalTemplate(settings.arrival.defaultTemplate)}>
              Use default text
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default SmsSettingsSection;
